import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Link2, Copy, ExternalLink, RefreshCw, Ban, Loader2, Send } from "lucide-react";
import * as api from "@/lib/api";

export default function PatientLinkButton({ patientId, patientName }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const url = link?.active ? api.patientLinkUrl(link.path) : "";

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { setLink(await fn()); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };
  const onOpen = (v) => { setOpen(v); if (v) run(() => api.getPatientLink(patientId)); };
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copiato"); } catch (e) { toast.error("Copia non riuscita"); } };
  const regenerate = () => {
    if (link?.active && !window.confirm("Rigenerando, il link attuale smetterà di funzionare. Continuare?")) return;
    run(() => api.createPatientLink(patientId), link?.active ? "Nuovo link generato" : "Link generato");
  };
  const revoke = () => { if (window.confirm("Revocare il link? Il paziente non potrà più accedere.")) run(() => api.revokePatientLink(patientId), "Link revocato"); };
  const tg = () => window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(`Ciao ${patientName}, ecco la tua area personale AGFit`)}`, "_blank", "noopener");

  if (!patientId) return null;
  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1.5 rounded-lg border border-violet-400/40 bg-violet-400/10 px-3 py-2 text-sm font-medium text-violet-300 transition-colors hover:bg-violet-400/20" data-testid="patient-link-btn">
          <Link2 className="h-4 w-4" /> Link personale paziente
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 border-white/10 bg-[hsl(var(--popover))] text-foreground" data-testid="patient-link-popover">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">Link personale</div>
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : (
            <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${link?.active ? "bg-primary/15 text-primary" : "bg-white/10 text-muted-foreground"}`} data-testid="patient-link-status">{link?.active ? "ATTIVO" : "NON ATTIVO"}</span>
          )}
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">Dieta, allenamento e analisi video sempre aggiornati. Il link resta valido finché non lo revochi o elimini il paziente.</p>
        {link?.active && (
          <div className="mt-3 space-y-2">
            <div className="break-all rounded-lg border border-white/10 bg-black/30 px-2.5 py-2 font-mono-num text-[11px]" data-testid="patient-link-url">{url}</div>
            {link.created_at && <div className="text-[10px] text-muted-foreground">Creato il {new Date(link.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</div>}
            <div className="flex flex-wrap gap-1.5">
              <button onClick={copy} className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground" data-testid="patient-link-copy"><Copy className="h-3.5 w-3.5" /> Copia</button>
              <button onClick={tg} className="inline-flex items-center gap-1 rounded-lg border border-[hsl(199_89%_55%)]/50 bg-[hsl(199_89%_55%)]/15 px-2.5 py-1.5 text-xs font-semibold text-[hsl(199_89%_68%)]" data-testid="patient-link-telegram"><Send className="h-3.5 w-3.5" /> Telegram</button>
              <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-2.5 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="patient-link-preview"><ExternalLink className="h-3.5 w-3.5" /> Anteprima</a>
            </div>
          </div>
        )}
        <div className="mt-3 flex gap-1.5 border-t border-white/5 pt-3">
          <button onClick={regenerate} disabled={busy} className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-violet-400/40 bg-violet-400/10 px-2.5 py-1.5 text-xs font-semibold text-violet-300 disabled:opacity-50" data-testid="patient-link-generate">
            <RefreshCw className="h-3.5 w-3.5" /> {link?.active ? "Rigenera" : "Genera link"}
          </button>
          {link?.active && (
            <button onClick={revoke} disabled={busy} className="inline-flex items-center gap-1 rounded-lg border border-destructive/40 px-2.5 py-1.5 text-xs font-semibold text-destructive disabled:opacity-50" data-testid="patient-link-revoke"><Ban className="h-3.5 w-3.5" /> Revoca</button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
